import React, { useEffect } from 'react';
import { getOneWeekBeforeDate } from '../../../../Utils/dates.util';
import HealthDataFilterForm, {
	HealthDataFilterFormValues,
} from '../../../HealthData/Components/Filter/HealthDataFilterForm';
import { HealthData } from '../../Enums/HealthData.enum';
import { HealthDataType } from '../../Enums/HealthDataType.enum';
import useHealthDataFilter from '../../../HealthData/Hooks/useHealthDataFilter';
import useHealthDataSource from '../../../HealthData/Hooks/useHealthDataSource';
import { babyHealthDataFilterScheme } from './BabyHealthDataFilterScheme';
import { BabyLatamMonitoring } from '../../../Questionnaire/Enums/BabyLatamMonitoring.enum';

interface Props {
	onSubmit: (values: HealthDataFilterFormValues) => void;
}

const BabyHealthDataFilterForm: React.FC<Props> = ({ onSubmit }) => {
	const { mapSources } = useHealthDataSource();
	const { getPrepareFilterData } = useHealthDataFilter();

	const defaultValues = {
		rom: getOneWeekBeforeDate(new Date()),
		to: new Date(),
		date: new Date(),
		view: 'day',
		dataType: HealthDataType.RAW,
		toggleBloodGlucose: true,
		glucoseView: 'allValues',
		toggleBloodPressure: true,
		toggleWeight: true,
		toggleVaginalBleeding: true,
		toggleTemperature: true,
		toggleUrinaryTractInfection: true,
		toggleVaginalInfection: true,
		toggleVomiting: true,
		toggleDiarrhoea: true,
		toggleSwelling: true,
		toggleMedicaments: true,
		toggleBloodpresure: true,
		toggleGlycemia: true,
		toggleAmnioticFluidLeak: true,
		toggleHypertensionSymptoms: true,
		toggleBreastSelfAssessment: true,
		toggleFetalMovements: true,
		toggleUterineContractions: true,
		source: {
			appleHealth: false,
			googleFit: false,
			questionnaire: true,
			ingestion: true,
		},
	};

	useEffect(() => {
		handleOnSubmit(defaultValues);
	}, []);

	const handleOnSubmit = (values: any) => {
		const keys: string[] = [];
		if (values.toggleBloodPressure) {
			keys.push(
				HealthData.BLOOD_PRESSURE_SYSTOLIC,
				HealthData.BLOOD_PRESSURE_DIASTOLIC,
				HealthData.HEARTH_RATE,
			);
		}
		if (values.toggleWeight) {
			keys.push(HealthData.WEIGHT);
		}
		if (values.toggleVaginalBleeding) {
			keys.push(BabyLatamMonitoring.VAGINAL_BLEEDING);
		}

		if (values.toggleTemperature) {
			keys.push(BabyLatamMonitoring.TEMPERATURE);
		}

		if (values.toggleUrinaryTractInfection) {
			keys.push(BabyLatamMonitoring.URINARY_TRACT_INFECTION);
		}

		if (values.toggleVaginalInfection) {
			keys.push(BabyLatamMonitoring.VAGINAL_INFECTION);
		}

		if (values.toggleVomiting) {
			keys.push(BabyLatamMonitoring.VOMITING);
		}

		if (values.toggleDiarrhoea) {
			keys.push(BabyLatamMonitoring.DIARRHOEA);
		}

		if (values.toggleSwelling) {
			keys.push(BabyLatamMonitoring.SWELLING);
		}

		if (values.toggleMedicaments) {
			keys.push(BabyLatamMonitoring.MEDICAMENTS);
		}

		if (values.toggleBloodpresure) {
			keys.push(BabyLatamMonitoring.BLOOD_PRESSURE);
		}

		if (values.toggleGlycemia) {
			keys.push(BabyLatamMonitoring.GLYCEMIA);
			keys.push(HealthData.BLOOD_GLUCOSE);
		}

		if (values.toggleAmnioticFluidLeak) {
			keys.push(BabyLatamMonitoring.AMNIOTIC_FLUID_LEAK);
		}

		if (values.toggleHypertensionSymptoms) {
			keys.push(BabyLatamMonitoring.HYPERTENSION_SYMPTOMS);
		}

		if (values.toggleBreastSelfAssessment) {
			keys.push(BabyLatamMonitoring.BREAST_SELF_ASSESSMENT);
		}

		if (values.toggleFetalMovements) {
			keys.push(BabyLatamMonitoring.FETAL_MOVEMENTS);
		}

		if (values.toggleUterineContractions) {
			keys.push(BabyLatamMonitoring.UTERINE_CONTRACTIONS);
		}

		if (values.toggleUterineContractions) {
			keys.push(BabyLatamMonitoring.ROUTE_OF_DELIVERY);
		}

		keys.push(BabyLatamMonitoring.COMBINED);

		const dataForFilter = getPrepareFilterData(
			values.view,
			values.date,
			values.from,
			values.to,
		);

		const filterValues: HealthDataFilterFormValues = {
			from: dataForFilter.from,
			to: dataForFilter.to,
			view: dataForFilter.view,
			source: mapSources(values.source),
			type:
				dataForFilter.view === 'day' ? values.dataType : HealthDataType.DEFAULT,
			keys: keys,
		};

		onSubmit(filterValues);
	};

	return (
		<HealthDataFilterForm
			onSubmit={handleOnSubmit}
			formScheme={babyHealthDataFilterScheme}
			defaultValues={defaultValues}
		/>
	);
};

export default BabyHealthDataFilterForm;
